/**
 * Create Trigger Button
 * Vanilla JS implementation for host page
 */

import type { LoaderConfig } from './types';

// Default chat bubble icon (white duotone - works on colored backgrounds)
const DEFAULT_CHAT_ICON_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="30 36 86 88" aria-hidden="true">
  <path opacity="0.4" d="M109.781 38.5645C111.646 38.5647 113.157 40.3097 113.157 42.4619V98.667C113.157 100.819 111.646 102.564 109.781 102.564H62.1455C59.0945 106.705 57.2822 112.47 57.2822 120.5H53.4219L49.4521 102.564H35.5332C33.6686 102.564 32.1572 100.819 32.1572 98.667V42.4619C32.1572 40.3096 33.6686 38.5645 35.5332 38.5645H109.781Z" fill="white"/>
  <path d="M54.572 76.144C57.6493 76.144 60.144 73.6493 60.144 70.572C60.144 67.4947 57.6493 65 54.572 65C51.4947 65 49 67.4947 49 70.572C49 73.6493 51.4947 76.144 54.572 76.144Z" fill="white"/>
  <path d="M72.8152 76.144C75.8925 76.144 78.3872 73.6493 78.3872 70.572C78.3872 67.4947 75.8925 65 72.8152 65C69.7378 65 67.2432 67.4947 67.2432 70.572C67.2432 73.6493 69.7378 76.144 72.8152 76.144Z" fill="white"/>
  <path d="M91.0603 76.144C94.1376 76.144 96.6323 73.6493 96.6323 70.572C96.6323 67.4947 94.1376 65 91.0603 65C87.983 65 85.4883 67.4947 85.4883 70.572C85.4883 73.6493 87.983 76.144 91.0603 76.144Z" fill="white"/>
</svg>
`;

// Position styles mapping
const POSITION_STYLES: Record<string, string> = {
  'bottom-right': 'bottom: 20px; right: 20px;',
  'bottom-left': 'bottom: 20px; left: 20px;',
  'top-right': 'top: 20px; right: 20px;',
  'top-left': 'top: 20px; left: 20px;',
};

export interface TriggerButtonAPI {
  element: HTMLButtonElement;
  show: () => void;
  hide: () => void;
  updateStyles: (config: LoaderConfig) => void;
  destroy: () => void;
}

export function createTrigger(config: LoaderConfig, onClick: () => void): TriggerButtonAPI | null {
  // No trigger button in inline mode - widget is always visible
  if (config.embedMode === 'inline') {
    return null;
  }

  const trigger = document.createElement('button');
  trigger.className = 'savanto-trigger';
  trigger.setAttribute('type', 'button');
  trigger.setAttribute('title', 'Chat with Assistant');
  trigger.setAttribute('aria-label', 'Open chat');
  trigger.setAttribute('data-sv-component', 'trigger-button');

  // Apply initial styles
  applyTriggerStyles(trigger, config);

  // Click handler
  trigger.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  };

  // Hover effects
  const theme = config.theme?.triggerButton || {};
  const showBackground = config.triggerShowBackground !== false;

  trigger.onmouseenter = () => {
    trigger.style.transform = 'scale(1.05)';
    if (showBackground) {
      trigger.style.background = theme.hoverBackground || config.theme?.colors?.primaryHover || '#334155';
      if (theme.hoverBorderColor) {
        trigger.style.borderColor = theme.hoverBorderColor;
      }
    }
  };

  trigger.onmouseleave = () => {
    trigger.style.transform = 'scale(1)';
    if (showBackground) {
      trigger.style.background = theme.background || config.theme?.colors?.primaryColor || '#475569';
      if (theme.borderColor) {
        trigger.style.borderColor = theme.borderColor;
      }
    }
  };

  // Append to body
  document.body.appendChild(trigger);

  return {
    element: trigger,
    show: () => {
      trigger.style.display = 'flex';
    },
    hide: () => {
      trigger.style.display = 'none';
    },
    updateStyles: (newConfig: LoaderConfig) => {
      applyTriggerStyles(trigger, newConfig);
    },
    destroy: () => {
      trigger.remove();
    },
  };
}

function applyTriggerStyles(trigger: HTMLButtonElement, config: LoaderConfig): void {
  const theme = config.theme?.triggerButton || {};
  const position = config.triggerPosition || 'bottom-right';
  const showBackground = config.triggerShowBackground !== false;
  const showBorder = config.triggerShowBorder !== false;
  const primaryColor = config.theme?.colors?.primaryColor || '#475569';

  // Clear existing content
  trigger.innerHTML = '';

  // Base styles
  trigger.style.cssText = `
    position: fixed;
    ${POSITION_STYLES[position] || POSITION_STYLES['bottom-right']}
    z-index: 999998;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    border: ${showBackground && theme.borderColor ? `1px solid ${theme.borderColor}` : 'none'};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    outline: none;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
    ${
      showBackground
        ? `background: ${theme.background || primaryColor}; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);`
        : 'background: transparent; box-shadow: none;'
    }
  `;

  // Avatar or default icon
  if (config.triggerIconUrl) {
    const img = document.createElement('img');
    img.src = config.triggerIconUrl;
    img.alt = 'Chat';
    img.style.cssText = `
      width: 40px;
      height: 40px;
      ${showBorder ? 'border-radius: 50%; border: 2px solid rgba(255, 255, 255, 0.3);' : 'border-radius: 0; border: none;'}
      object-fit: cover;
    `;
    img.onerror = () => {
      // Fallback to icon if image fails to load
      trigger.innerHTML = DEFAULT_CHAT_ICON_SVG;
      const svg = trigger.querySelector('svg');
      if (svg) {
        svg.style.cssText = `
          width: 28px;
          height: 28px;
          color: ${theme.iconColor || '#ffffff'};
        `;
      }
    };
    trigger.appendChild(img);
  } else {
    // Default chat icon
    trigger.innerHTML = DEFAULT_CHAT_ICON_SVG;
    const svg = trigger.querySelector('svg');
    if (svg) {
      (svg as SVGElement).style.cssText = `
        width: 28px;
        height: 28px;
        color: ${theme.iconColor || '#ffffff'};
      `;
    }
  }
}
